// Utility functions for formatting data

// Format currency with $ and 2 decimal places and with billing period suffix if provided
import { BillingPeriod } from "@/types/product";
import { billingPeriodSuffix } from "./productTypeUtils";

export const formatCurrency = (
  value?: number,
  _currency?: string,
  period?: BillingPeriod
): string => {
  // Handle undefined or null values
  if (value === undefined || value === null) {
    return '$0.00';
  }
  
  const formattedValue = `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  
  // Add period suffix if provided and not 'one_time'
  if (period && period !== 'one_time') {
    return `${formattedValue}${billingPeriodSuffix(period)}`;
  }
  
  return formattedValue;
};

// Format date to a readable format with time
export const formatDate = (dateInput: string | Date | number): string => {
  // Handle cases where the input is null or undefined
  if (!dateInput) {
    return 'N/A';
  }
  
  let date: Date;
  
  try {
    // Handle different types of date input
    if (typeof dateInput === 'number') {
      // If it's a timestamp number
      date = new Date(dateInput);
    } else if (dateInput instanceof Date) {
      // If it's already a Date object
      date = dateInput;
    } else {
      // If it's a string, parse it
      date = new Date(dateInput);
    }
    
    // Verify that the date is valid before formatting
    if (isNaN(date.getTime())) {
      return 'Invalid Date';
    }
    
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (error) {
    console.error('Error formatting date:', error, dateInput);
    return 'Invalid Date';
  }
};

// Get day suffix (st, nd, rd, th)
export function getDaySuffix(day: number): string {
  if (day >= 11 && day <= 13) return 'th';
  
  switch (day % 10) {
    case 1: return 'st';
    case 2: return 'nd';
    case 3: return 'rd';
    default: return 'th';
  }
}

// Format product ID to ensure it has prod_ prefix
export function formatProductId(id: string): string {
  if (!id) return '';
  return id.startsWith('prod_') ? id : `prod_${id}`;
}

// Format subscription ID to ensure it has sub_ prefix
export function formatSubscriptionId(id: string): string {
  if (!id) return '';
  return id.startsWith('sub_') ? id : `sub_${id}`;
}

// Format offer ID to ensure it has off_ prefix
export function formatOfferId(id: string): string {
  if (!id) return '';
  // Strip any existing prefix patterns like 'offer_offerid_' or 'offer_'
  const cleanId = id.replace(/^offer_offerid_|^offer_/, '');
  return id.startsWith('off_') ? id : `off_${cleanId}`;
}
